import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  FileText,
  DollarSign,
  Phone,
  Headphones,
  Key,
  Workflow,
  Activity,
  Plug,
  Settings,
  BarChart3,
  Terminal,
  Database,
  LayoutTemplate,
  Table2,
  Globe,
  FileSearch,
  TrendingUp,
  Bot,
} from 'lucide-react';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { ENTERPRISE_ADMIN_BASE } from '../../config/adminLayers';

interface EnterpriseSidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

type NavEntry = { label: string; to: string; icon: React.ReactNode; end?: boolean };

const section = (title: string, items: NavEntry[]) => ({ title, items });

const E = ENTERPRISE_ADMIN_BASE;

const NAV_SECTIONS = [
  section('Overview', [
    { label: 'Command overview', to: E, icon: <LayoutDashboard className="h-4 w-4" />, end: true },
    { label: 'Ops dashboard', to: `${E}/dashboard`, icon: <TrendingUp className="h-4 w-4" /> },
  ]),
  section('Directory', [
    { label: 'Users', to: `${E}/users`, icon: <Users className="h-4 w-4" /> },
    {
      label: 'Workspace directory',
      to: `${E}/workspaces`,
      icon: <Building2 className="h-4 w-4" />,
    },
    { label: 'Workspaces (table)', to: `${E}/workspaces/table`, icon: <Table2 className="h-4 w-4" /> },
  ]),
  section('Revenue & access', [
    { label: 'Invoices', to: `${E}/invoices`, icon: <FileText className="h-4 w-4" /> },
    { label: 'Pricing', to: `${E}/pricing`, icon: <DollarSign className="h-4 w-4" /> },
  ]),
  section('Settings', [
    { label: 'Settings', to: `${E}/settings`, icon: <Settings className="h-4 w-4" /> },
    {
      label: 'Invoice templates',
      to: `${E}/settings/invoice-templates`,
      icon: <LayoutTemplate className="h-4 w-4" />,
    },
  ]),
  section('Platform', [
    { label: 'Sending domains', to: `${E}/verified-domains`, icon: <Globe className="h-4 w-4" /> },
    { label: 'API Keys', to: `${E}/api-keys`, icon: <Key className="h-4 w-4" /> },
    { label: 'Phone numbers', to: `${E}/phone-numbers`, icon: <Phone className="h-4 w-4" /> },
    { label: 'Calling providers', to: `${E}/calling-providers`, icon: <Headphones className="h-4 w-4" /> },
    { label: 'Workflows', to: `${E}/workflows`, icon: <Workflow className="h-4 w-4" /> },
    { label: 'Workflow health', to: `${E}/workflow-health`, icon: <Activity className="h-4 w-4" /> },
    { label: 'Integrations', to: `${E}/integrations`, icon: <Plug className="h-4 w-4" /> },
  ]),
  section('Compliance', [
    { label: 'Audit log', to: `${E}/audit`, icon: <FileSearch className="h-4 w-4" /> },
  ]),
  section('Reliability', [
    { label: 'System', to: `${E}/system`, icon: <Settings className="h-4 w-4" /> },
    { label: 'Monitoring', to: `${E}/monitoring`, icon: <BarChart3 className="h-4 w-4" /> },
    { label: 'FastAPI', to: `${E}/fastapi-monitoring`, icon: <Terminal className="h-4 w-4" /> },
    { label: 'Copilot metrics', to: `${E}/copilot-metrics`, icon: <BarChart3 className="h-4 w-4" /> },
    { label: 'Jarvis tools', to: `${E}/jarvis-tools`, icon: <Bot className="h-4 w-4" /> },
    { label: 'Supabase', to: `${E}/supabase-monitor`, icon: <Database className="h-4 w-4" /> },
  ]),
];

export const EnterpriseSidebar: React.FC<EnterpriseSidebarProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const { logout } = useAdminAuth();

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-enterprise-accent/15 text-enterprise-accent'
        : 'text-enterprise-200 hover:bg-enterprise-800/80 hover:text-white'
    }`;

  return (
    <>
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="fixed inset-0 bg-black/60"
            aria-label="Close menu"
            onClick={() => setSidebarOpen(false)}
          />
        </div>
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-enterprise-700/80 bg-gradient-to-b from-enterprise-950 to-enterprise-900 shadow-2xl transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 shrink-0 items-center gap-2 border-b border-enterprise-700/80 px-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-enterprise-accent/20 ring-1 ring-enterprise-accent/30">
            <LayoutDashboard className="h-5 w-5 text-enterprise-accent" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-enterprise-400">SWS CRM</p>
            <p className="text-sm font-bold text-white">Enterprise Admin</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {NAV_SECTIONS.map((block) => (
            <div key={block.title} className="mb-6">
              <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-widest text-enterprise-500">
                {block.title}
              </p>
              <ul className="space-y-0.5">
                {block.items.map((item) => (
                  <li key={item.label}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={navClass}
                      onClick={() => setSidebarOpen(false)}
                    >
                      {item.icon}
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-enterprise-700/80 p-4">
          <Link
            to="/admin/dashboard"
            onClick={() => setSidebarOpen(false)}
            className="mb-3 flex items-center justify-center gap-2 rounded-lg border border-enterprise-600 bg-enterprise-800/40 px-3 py-2 text-xs font-medium text-enterprise-100 hover:bg-enterprise-800"
          >
            <LayoutTemplate className="h-4 w-4" />
            Open classic sidebar
          </Link>
          <button
            type="button"
            onClick={logout}
            className="w-full rounded-md px-3 py-2 text-left text-sm text-red-400 hover:bg-red-950/40"
          >
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
};
